import React, { useState, useEffect, useRef } from 'react';
import { runPrefetch, requestPersistOnce } from '../services/prefetch';

// Module-level store so download state persists across component mounts.
// This prevents the mobile-menu chip from aborting a running download when
// the user closes the menu (which unmounts that chip instance).
const _store = {
  status: 'idle',  // 'idle' | 'running' | 'done' | 'error'
  progress: { phase: 'daily', fetched: 0, total: 0, errors: 0 },
  denialThisRun: false,
  abortController: null,
  listeners: new Set(),
};

function _snap() {
  return {
    status: _store.status,
    progress: { ..._store.progress },
    denialThisRun: _store.denialThisRun,
  };
}

function _notify() {
  const s = _snap();
  _store.listeners.forEach(fn => fn(s));
}

// Exported for unit tests only.
export const __resetStore = () => {
  _store.abortController?.abort();
  _store.status = 'idle';
  _store.progress = { phase: 'daily', fetched: 0, total: 0, errors: 0 };
  _store.denialThisRun = false;
  _store.abortController = null;
  _store.listeners.clear();
};

function runningLabel(p) {
  const errs = p.errors > 0 ? ` · ${p.errors} failed` : '';
  switch (p.phase) {
    case 'daily':      return `Loading daily…${errs}`;
    case 'digests':    return `Loading digests…${errs}`;
    case 'articles':   return `Downloading… ${p.fetched}/${p.total} articles${errs}`;
    case 'highlights': return `Loading highlights…${errs}`;
    default:           return `Downloading…${errs}`;
  }
}

const DownloadForOfflineChip = ({ variant = 'header' }) => {
  const [storeSnap, setStoreSnap] = useState(_snap);
  const collapseTimerRef = useRef(null);

  useEffect(() => {
    _store.listeners.add(setStoreSnap);
    // Sync on mount; if download finished while chip was unmounted, start collapse.
    const current = _snap();
    setStoreSnap(current);
    if (current.status === 'done') {
      collapseTimerRef.current = setTimeout(() => {
        _store.status = 'idle';
        _notify();
      }, 4000);
    }
    return () => {
      _store.listeners.delete(setStoreSnap);
      clearTimeout(collapseTimerRef.current);
      // Reason: intentionally NOT aborting — download must survive menu close.
    };
  }, []);

  const { status, progress, denialThisRun } = storeSnap;

  async function handleClick() {
    if (status === 'running') return;
    clearTimeout(collapseTimerRef.current);

    _store.abortController?.abort();
    const ac = new AbortController();
    _store.abortController = ac;
    _store.status = 'running';
    _store.progress = { phase: 'daily', fetched: 0, total: 0, errors: 0 };
    _store.denialThisRun = false;
    _notify();

    const persist = await requestPersistOnce().catch(() => ({ denied: false, alreadyAsked: true }));
    // Guard: if a second click started a newer download, bail.
    if (ac.signal.aborted) return;
    if (persist.denied && !persist.alreadyAsked) {
      _store.denialThisRun = true;
      _notify();
    }

    let result = null;
    try {
      result = await runPrefetch({
        signal: ac.signal,
        onProgress: (p) => {
          _store.progress = { ...p };
          _notify();
        },
      });
    } catch {
      // unexpected throw from runPrefetch
    } finally {
      _store.abortController = null;
    }

    if (ac.signal.aborted) return;

    if (!result || result.errors > 0) {
      _store.status = 'error';
      _notify();
    } else {
      _store.status = 'done';
      _notify();
      collapseTimerRef.current = setTimeout(() => {
        _store.status = 'idle';
        _notify();
      }, 4000);
    }
  }

  const isMenu = variant === 'menu';
  const baseBtn = isMenu
    ? 'flex items-center w-full px-4 py-2.5 text-sm text-body-text hover:bg-clay-bg transition-colors'
    : 'pill-clay text-sm whitespace-nowrap';

  if (status === 'running') {
    return (
      <span className={isMenu ? baseBtn + ' cursor-default' : 'pill-clay-active text-sm whitespace-nowrap'}>
        <span className="spinner-fun-sm mr-2" aria-hidden="true" />
        {runningLabel(progress)}
      </span>
    );
  }

  if (status === 'done') {
    return (
      <span className={isMenu ? baseBtn + ' cursor-default' : 'badge-fun-mint text-sm whitespace-nowrap'}>
        Ready for offline ✓
        {denialThisRun && (
          <span className="ml-2 text-xs text-secondary-text">
            (storage not persistent)
          </span>
        )}
      </span>
    );
  }

  if (status === 'error') {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={isMenu ? baseBtn : 'badge-fun-coral text-sm whitespace-nowrap'}
        aria-label="Retry download for offline"
      >
        {isMenu && (
          <svg className="w-4 h-4 mr-3 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
          </svg>
        )}
        Download incomplete — Retry
      </button>
    );
  }

  // idle
  return (
    <button
      type="button"
      onClick={handleClick}
      className={baseBtn}
      aria-label="Download for offline"
    >
      {isMenu && (
        <svg className="w-4 h-4 mr-3 text-tertiary-text" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
        </svg>
      )}
      Download for offline
    </button>
  );
};

export default DownloadForOfflineChip;
